import { FC, useMemo } from "react";
import {
	Alert,
	Box,
	Button,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Link,
	TextField,
	Typography,
} from "@mui/material";
import WaterDropIcon from "@mui/icons-material/WaterDrop";

import ConfigEditorShell from "../../components/ConfigEditorShell/ConfigEditorShell";
import {
	type ExitRoute,
	useConfigEditor,
} from "../../components/ConfigEditorShell/useConfigEditor";
import {
	AnalyteStatus,
	AnalyteType,
	IAnalyte,
} from "../../domain/analyte.interface";
import { CupResponse } from "../../domain/cup.interface";
import { useCreateAnalyte, useUpdateAnalyte } from "../../hooks/useAnalytes";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import {
	type AnalyteDraft,
	analyteDraftFrom,
	canSaveAnalyte,
	duplicateShortnameMessage,
	emptyAnalyteDraft,
	normaliseShortnameInput,
	renameImpactMessage,
	toAnalytePayload,
} from "./analyteDraft";
import { analyteSaveEntries, useCupsAudit } from "./cupsAudit";
import { cupsCarryingList } from "./analytesList";

/**
 * SIMS-8814 — the analyte editor.
 *
 * Same shape as `CupEditor`: `ConfigEditorShell` supplies the breadcrumb, the sticky
 * Cancel/Save header, the unsaved-changes pill, the discard prompt and the save-failed
 * banner, so those states need no code here.
 *
 * AC4 — THE EDITOR NEVER WRITES A DISPLAY NAME. A blank display is sent empty and the
 * service resolves it on read. The Hi-Fi does the opposite (`commitAna` markup 1140
 * stores `display: d.display || d.code`), and copying it would leave the old shortname in
 * the Display column the first time anyone renames an analyte — the very harm AC3 exists
 * to warn about. See `analyteDraft.ts`.
 *
 * Delete in the header is SIMS-8853: the editor only asks — the container owns the
 * confirmation, so the list and the editor reach the same dialog. Deliberately NOT here: the
 * create-from-a-cup flow (SIMS-8806, FR-39). The editor is shaped so 8806 can reuse it
 * rather than fork it.
 */
export interface AnalyteEditorProps {
	/** Undefined for a new analyte. */
	analyte?: IAnalyte;
	/** The cup catalogue, used to list what carries this analyte (AC3). */
	cups: CupResponse[];
	/** The cups query has not settled — usage is unknown, not empty. */
	cupsUnavailable?: boolean;
	onExit: () => void;
	/** SIMS-8853 — ask to delete this analyte. Shown for a saved analyte only (markup L1454). */
	onDelete?: (analyte: IAnalyte) => void;
	/**
	 * SIMS-8806 — set when this form was opened from a cup, which the container is holding.
	 * Every exit (`onExit`) then returns to that cup, and a created analyte is handed back
	 * through `onCreated` instead of the form closing on its own.
	 */
	heldCupName?: string;
	onCreated?: (analyte: IAnalyte) => void;
	/** SIMS-9266 — open a carrying cup's editor from the Used-in-cups table. */
	onOpenCup?: (cup: CupResponse) => void;
}

// SIMS-9264 — the Hi-Fi puts each label above its input (L721-737). MUI hides a placeholder
// while the label floats inside the field, so without this an empty field reads blank until focus.
const LABEL_ABOVE = { shrink: true } as const;

// The Hi-Fi always pluralises (L1446); "1 analytes" is not kept.
export const analyteCountLabel = (analytes: unknown): string => {
	const n = Array.isArray(analytes) ? analytes.length : 0;
	return n === 1 ? "1 analyte" : `${n} analytes`;
};

const TYPE_CHOICES: AnalyteType[] = [
	AnalyteType.STIMULANT,
	AnalyteType.OTHER_SUBSTANCE,
];

/**
 * The two the design draws, PLUS whatever this record already carries.
 *
 * A record whose type is outside the pair would otherwise render with no segment
 * selected and no explanation, and — because the draft preserves the value — Save would
 * re-send it and the backend's `@IsEnum` would answer 400, which the shell reports only
 * as "The analyte could not be saved.". The analyte becomes uneditable with nothing on
 * screen saying why.
 *
 * Only reachable for data written before the model enforced the enum, and it may be that
 * no such record exists. But `analytesList.ts:typeFilterOptions` derives its filter chips
 * from the data precisely because the legacy five-type set is treated as a live
 * possibility, and the two files should not disagree about that.
 */
const typeChoicesFor = (current?: string): string[] =>
	current && !TYPE_CHOICES.includes(current as AnalyteType)
		? [...TYPE_CHOICES, current]
		: TYPE_CHOICES;

const STATUS_CHOICES: { value: string; label: string }[] = [
	{ value: AnalyteStatus.ACTIVE, label: "Active" },
	{ value: AnalyteStatus.INACTIVE, label: "Inactive" },
];

/** Same reasoning as the type choices — a status outside the pair renders blank. */
const statusChoicesFor = (
	current?: string,
): { value: string; label: string }[] =>
	current && !STATUS_CHOICES.some((c) => c.value === current)
		? [...STATUS_CHOICES, { value: current, label: current }]
		: STATUS_CHOICES;

const AnalyteEditor: FC<AnalyteEditorProps> = ({
	analyte,
	cups,
	cupsUnavailable = false,
	onExit,
	onDelete,
	heldCupName,
	onCreated,
	onOpenCup,
}) => {
	const holding = heldCupName !== undefined;
	const isNew = !analyte;
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const createAnalyte = useCreateAnalyte();
	const updateAnalyte = useUpdateAnalyte();
	const recordAudit = useCupsAudit();

	const editor = useConfigEditor<AnalyteDraft>({
		initial: analyte ? analyteDraftFrom(analyte) : emptyAnalyteDraft(),
		fallbackSaveError: "The analyte could not be saved.",
		onSave: async (draft) => {
			const payload = toAnalytePayload(
				draft,
				process.env.REACT_APP_ACCOUNT_ID || "",
				userInfo?.id || "",
			);
			if (analyte) {
				const { createdBy, ...rest } = payload;
				await updateAnalyte.mutateAsync({
					...rest,
					_id: analyte._id,
					updatedBy: createdBy,
				});
				await recordAudit(analyteSaveEntries(analyteDraftFrom(analyte), draft));
			} else {
				const created = await createAnalyte.mutateAsync(payload);
				// SIMS-8861 — the held-cup create (SIMS-8806) is logged here; the cup's own
				// composition entry is written when that cup is saved.
				await recordAudit(analyteSaveEntries(null, draft));
				if (holding && onCreated) {
					// The container returns to the cup and says what happened there.
					onCreated(created);
					return;
				}
			}
			showSnackBar(
				analyte
					? "Analyte updated successfully"
					: "Analyte created successfully",
				"success",
			);
			onExit();
		},
	});

	const { draft, setField } = editor;
	const leave = (route: ExitRoute): void => editor.requestExit(onExit, route);

	// AC2 — a 409 from the uniqueness index is the shortname being taken, not a generic
	// failure. Everything else falls through to the shell's own message.
	const conflict = duplicateShortnameMessage(
		(createAnalyte.error ?? updateAnalyte.error) as unknown,
	);

	// AC3 — derived from the cups the screen already holds; no round trip.
	const carrying = useMemo(
		() => (analyte ? cupsCarryingList(analyte, cups) : []),
		[analyte, cups],
	);

	return (
		<ConfigEditorShell
			breadcrumbs={[
				{ label: "Program setup" },
				{ label: "Cups & analytes", onClick: () => leave("breadcrumb") },
				{ label: "Analytes", onClick: () => leave("breadcrumb") },
			]}
			// The design titles an existing analyte by its SCIENTIFIC name while the
			// breadcrumb leaf carries the shortname (markup 1322-1323). Followed as drawn,
			// though the ACs are shortname-centric.
			title={isNew ? "New analyte" : (analyte?.scientificName ?? "Analyte")}
			isNew={isNew}
			onDelete={analyte && onDelete ? () => onDelete(analyte) : undefined}
			isDirty={editor.isDirty}
			isSaving={editor.isSaving}
			canSave={editor.canSave && canSaveAnalyte(draft)}
			saveError={conflict ?? editor.saveError}
			failedTitle="Analyte wasn't saved"
			discardBody={
				holding
					? `The analyte has not been created. Discarding returns you to ${heldCupName} with your cup edits intact.`
					: "This analyte has changes that have not been saved. Discarding closes the editor and leaves the analyte as it was."
			}
			saveLabel={holding ? "Save and return to cup" : undefined}
			discardPromptOpen={editor.discardPromptOpen}
			onSave={editor.save}
			onCancel={() => leave("cancel")}
			onBreadcrumb={() => leave("breadcrumb")}
			onKeepEditing={editor.keepEditing}
			onDiscard={editor.discard}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{holding && (
					// SIMS-8806 — design L699-705: where this analyte is going, and the way back.
					<Alert
						severity="info"
						data-testid="held-cup-banner"
						action={
							<Button size="small" onClick={() => leave("cancel")}>
								Back to cup without saving
							</Button>
						}
					>
						{`Creating this analyte from ${heldCupName}. Saving adds it to that cup and takes you back with every edit intact.`}
					</Alert>
				)}
				<Box>
					<Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 2 }}>
						Analyte details
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							gap: 2,
						}}
					>
						<TextField
							autoFocus={holding}
							label="Shortname"
							required
							value={draft.shortname}
							// AC2 — upper-cased as the user types, as the design does. The service
							// upper-cases again on write; this transform is convenience, not the rule.
							onChange={(e) =>
								setField("shortname", normaliseShortnameInput(e.target.value))
							}
							placeholder="AMP"
							InputLabelProps={LABEL_ABOVE}
							inputProps={{ style: { fontFamily: "monospace" } }}
							helperText="Stored in upper case, and unique across the account."
						/>

						<TextField
							label="Scientific name"
							required
							value={draft.scientificName}
							onChange={(e) => setField("scientificName", e.target.value)}
							placeholder="Amphetamines"
							InputLabelProps={LABEL_ABOVE}
						/>

						{/* AC4 — both optional. The design gives these only a placeholder, which
						    disappears the moment the user types; the cup editor's equivalent field
						    carries helper text (markup 601) and these should too, or the rule is
						    invisible at the moment it matters. */}
						<TextField
							label="Shortname display"
							value={draft.shortnameDisplay}
							onChange={(e) => setField("shortnameDisplay", e.target.value)}
							placeholder="Same as shortname"
							InputLabelProps={LABEL_ABOVE}
							helperText="Defaults to the shortname when left blank."
						/>

						<TextField
							label="Scientific display"
							value={draft.scientificDisplay}
							onChange={(e) => setField("scientificDisplay", e.target.value)}
							placeholder="Same as scientific name"
							InputLabelProps={LABEL_ABOVE}
							helperText="Defaults to the scientific name when left blank."
						/>

						<Box>
							<Typography
								sx={{
									fontSize: "0.75rem",
									color: "text.secondary",
									mb: 0.5,
								}}
								id="analyte-type-label"
							>
								Type *
							</Typography>
							{/* Segmented buttons, not a select — the design draws them that way
							    (markup 742-744). */}
							<Box
								sx={{ display: "flex", gap: 1 }}
								role="radiogroup"
								aria-labelledby="analyte-type-label"
							>
								{typeChoicesFor(draft.type as string).map((choice) => (
									<Button
										key={choice}
										role="radio"
										aria-checked={draft.type === choice}
										variant={draft.type === choice ? "contained" : "outlined"}
										onClick={() => setField("type", choice as AnalyteType)}
										// SIMS-9265 — option labels, not buttons: sentence case, and two
										// separate 40px pills (markup L738-744).
										sx={{
											height: "2.5rem",
											px: 2,
											borderRadius: "0.5rem",
											fontSize: "0.8125rem",
											fontWeight: 500,
											textTransform: "none",
											letterSpacing: 0,
										}}
									>
										{choice}
									</Button>
								))}
							</Box>
						</Box>

						<FormControl>
							<InputLabel id="analyte-status-label">Status</InputLabel>
							<Select
								labelId="analyte-status-label"
								label="Status"
								value={draft.status}
								onChange={(e) =>
									setField("status", e.target.value as AnalyteStatus)
								}
							>
								{statusChoicesFor(draft.status as string).map((choice) => (
									<MenuItem key={choice.value} value={choice.value}>
										{choice.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				</Box>

				{/* AC3 — SUPPRESSED while the analyte is new, for two reasons.
				    The design renders this card on a brand-new record showing "Renaming this
				    analyte affects no cup." and "Not used by any cup yet." (cups-09, cups-10),
				    which says nothing true about a record that does not exist.
				    And it would be actively WRONG: `cupCarries` falls back to matching by
				    shortname when either id is absent, and a new analyte has no `_id` — so the
				    card would list every cup already carrying that shortname, cups this record
				    is demonstrably not on. SIMS-8806 reuses this editor for create-from-a-cup
				    and would meet that directly. */}
				{!isNew && (
					<Box>
						<Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 1 }}>
							Used in cups
						</Typography>
						<Typography
							sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 2 }}
							data-testid="analyte-rename-impact"
						>
							{cupsUnavailable
								? "What carries this analyte could not be loaded, so the effect of a rename is unknown."
								: renameImpactMessage(carrying.length)}
						</Typography>

						{cupsUnavailable ? null : carrying.length === 0 ? (
							<Box
								sx={{
									border: "1px dashed",
									borderColor: "divider",
									borderRadius: "0.5rem",
									p: 2,
								}}
							>
								<Typography
									sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
								>
									Not used by any cup yet.
								</Typography>
							</Box>
						) : (
							<Box
								component="table"
								data-testid="analyte-usage-table"
								sx={{ width: "100%", borderCollapse: "collapse" }}
							>
								{/* The design's rows carry a bare vendor string beside a bare
								    "14 analytes" with no header row at all (markup 766-778). Headed
								    here — the columns are unreadable otherwise. */}
								<Box component="thead">
									<Box component="tr">
										{[
											"Cup",
											"Vendor",
											"Analytes",
											...(onOpenCup ? [""] : []),
										].map((h) => (
											<Box
												component="th"
												key={h || "open"}
												sx={{
													textAlign: "left",
													fontSize: "0.6875rem",
													fontWeight: 600,
													letterSpacing: "0.08em",
													textTransform: "uppercase",
													color: "text.secondary",
													borderBottom: "1px solid",
													borderColor: "divider",
													py: 1,
												}}
											>
												{h}
											</Box>
										))}
									</Box>
								</Box>
								<Box component="tbody">
									{carrying.map((cup) => (
										<Box
											component="tr"
											key={cup._id}
											sx={{
												borderBottom: "1px solid",
												borderColor: "divider",
											}}
										>
											<Box component="td" sx={{ py: 1.25 }}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}
												>
													<WaterDropIcon fontSize="small" />
													<Typography sx={{ fontSize: "0.875rem" }}>
														{cup.name}
													</Typography>
												</Box>
											</Box>
											<Box component="td" sx={{ py: 1.25 }}>
												<Typography
													sx={{
														fontSize: "0.8125rem",
														color: "text.secondary",
													}}
												>
													{cup.display || cup.vendor}
												</Typography>
											</Box>
											<Box component="td" sx={{ py: 1.25 }}>
												{/* SIMS-9267 — plain text, as the Hi-Fi's rows (L1446). */}
												<Typography
													sx={{
														fontSize: "0.8125rem",
														color: "text.secondary",
													}}
												>
													{analyteCountLabel(cup.analytes)}
												</Typography>
											</Box>
											{onOpenCup && (
												<Box
													component="td"
													sx={{ py: 1.25, textAlign: "right" }}
												>
													{/* SIMS-9266 (L776). Through requestExit, so unsaved changes
													    get the same discard prompt as Cancel. */}
													<Link
														component="button"
														type="button"
														underline="none"
														aria-label={`Open cup ${cup.name}`}
														onClick={() =>
															editor.requestExit(
																() => onOpenCup(cup),
																"navigate",
															)
														}
														sx={{ fontSize: "0.8125rem" }}
													>
														Open cup
													</Link>
												</Box>
											)}
										</Box>
									))}
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</ConfigEditorShell>
	);
};

export default AnalyteEditor;
